import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, FileText, TrendingUp, Mail, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, V1 } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [apps, setApps] = useState([]);
  const [inv, setInv] = useState([]);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    if (user?.role !== "parent") return;
    api.get(`${V1}/children`).then((r) => setChildren(r.data || [])).catch(() => {});
    api.get(`${V1}/scholarship-applications/mine`).then((r) => setApps(r.data || [])).catch(() => {});
    api.get(`${V1}/invitations/mine`).then((r) => setInv(r.data || [])).catch(() => {});
    api.get(`${V1}/performance/me`).then((r) => setPerf(r.data || null)).catch(() => {});
  }, [user?.role]);

  return (
    <div className="space-y-8" data-testid="parent-dashboard">
      <div>
        <div className="overline text-accent mb-2">Panelim</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Merhaba, {user?.name?.split(" ")[0]} 👋
        </h1>
        <p className="text-muted-foreground mt-2">Çocuğunuzun bursluluk yolculuğuna hoş geldiniz.</p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Çocuk Sayısı" value={children.length} to="/panel/cocuklarim" />
        <MetricCard icon={FileText} label="Bursluluk Başvurusu" value={apps.length} to="/panel/basvurularim" />
        <MetricCard icon={Mail} label="Okul Daveti" value={inv.length} to="/panel/davetler" />
        <MetricCard icon={TrendingUp} label="Genel Ortalama" value={perf ? `%${perf.overall_average}` : "—"} to="/panel/performans" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-lg">Son Başvurular</div>
            <Link to="/panel/basvurularim" className="text-xs text-primary hover:underline">Tümü <ArrowRight className="h-3 w-3 inline" /></Link>
          </div>
          <div className="space-y-3">
            {apps.slice(0, 4).map((a) => (
              <div key={a.id} className="p-3 rounded border border-border">
                <div className="text-xs text-muted-foreground">{a.school?.name}</div>
                <div className="font-medium text-sm">{a.exam?.title}</div>
                <div className="flex justify-between mt-1 text-xs">
                  <span>{a.child?.name}</span>
                  <span className="px-2 py-0.5 bg-muted rounded uppercase tracking-widest text-[10px]">{a.status}</span>
                </div>
              </div>
            ))}
            {apps.length === 0 && <div className="text-sm text-muted-foreground">Henüz başvuru yapılmadı.</div>}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-lg">Okul Davetleri</div>
            <Link to="/panel/davetler" className="text-xs text-primary hover:underline">Tümü</Link>
          </div>
          <div className="space-y-3">
            {inv.slice(0, 4).map((i) => (
              <div key={i.id} className="p-3 rounded border border-border">
                <div className="text-xs text-muted-foreground">{i.school?.name}</div>
                <div className="text-sm font-medium">%{i.scholarship_rate} {i.scholarship_type}</div>
                <div className="text-xs mt-1">{i.child?.name}</div>
              </div>
            ))}
            {inv.length === 0 && <div className="text-sm text-muted-foreground">Henüz davet yok. Denemelere düzenli katılım artırır!</div>}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-primary text-primary-foreground p-8 grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="overline text-accent mb-2">AI Tercih Danışmanlığı</div>
          <div className="font-display text-2xl font-bold mb-2">LGS veya YKS puanına göre öneriler alın.</div>
          <p className="text-sm text-primary-foreground/80">Yapay zekâ; zorlayıcı, gerçekçi ve güvenli seçenekleri gerekçesiyle sunar.</p>
        </div>
        <div>
          <Button asChild size="lg" variant="secondary" className="bg-accent text-accent-foreground hover:bg-accent/90 w-full">
            <Link to="/panel/tercih-danismanligi"><Sparkles className="h-4 w-4" /> Danışmanlık Talep Et</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

const MetricCard = ({ icon: Icon, label, value, to }) => (
  <Link to={to} className="p-6 rounded-xl border border-border bg-card hover:border-primary/40 transition-colors" style={{ transition: "border-color 0.2s ease" }}>
    <Icon className="h-6 w-6 text-accent mb-3" />
    <div className="text-2xl font-display font-bold">{value}</div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </Link>
);
